import { useState, type ReactNode } from 'react';
import { Card, ErrorBanner, Loading, Spinner, StatusBadge } from '../../components/ui.js';
import { api, type SubscriptionStatus } from '../../lib/api.js';
import { formatDate, formatMoney, formatNumber, minorUnitExponent } from '../../lib/format.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { AdminLayout } from './AdminLayout.js';

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  memberCount: number;
  planSlug: string | null;
  subscriptionStatus: SubscriptionStatus | null;
}

/** Cross-tenant view. Opening a tenant is recorded in the audit log. */
export function AdminTenantsPage(): ReactNode {
  return <AdminLayout>{(staff) => <Tenants canWrite={staff.role === 'superadmin'} />}</AdminLayout>;
}

function Tenants({ canWrite }: { canWrite: boolean }): ReactNode {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const tenants = useAsync(
    () =>
      api.get<{ data: TenantRow[]; total: number }>(
        `/api/admin/tenants?limit=50&search=${encodeURIComponent(search)}`,
      ),
    [search],
  );

  if (tenants.loading && !tenants.data) return <Loading />;

  return (
    <div className="stack">
      <ErrorBanner error={tenants.error} />

      <Card>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or slug"
          aria-label="Search tenants"
        />
        <table style={{ marginTop: '1rem' }}>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Plan</th>
              <th style={{ textAlign: 'right' }}>Members</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(tenants.data?.data ?? []).map((tenant) => (
              <tr key={tenant.id}>
                <td>
                  <strong>{tenant.name}</strong>
                  <div className="faint mono small">/{tenant.slug}</div>
                </td>
                <td>
                  {tenant.planSlug ? (
                    <span className="row" style={{ gap: '0.4rem' }}>
                      <span style={{ textTransform: 'capitalize' }}>{tenant.planSlug}</span>
                      {tenant.subscriptionStatus && (
                        <StatusBadge status={tenant.subscriptionStatus} />
                      )}
                    </span>
                  ) : (
                    <span className="faint">Free</span>
                  )}
                </td>
                <td className="num">{tenant.memberCount}</td>
                <td className="muted small">{formatDate(tenant.createdAt)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="button-ghost"
                    onClick={() => setSelected(selected === tenant.id ? null : tenant.id)}
                  >
                    {selected === tenant.id ? 'Close' : 'Inspect'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(tenants.data?.data.length ?? 0) === 0 && (
          <p className="muted small" style={{ marginTop: '0.75rem' }}>
            No organizations match.
          </p>
        )}
      </Card>

      {selected && <TenantDetail organizationId={selected} canWrite={canWrite} />}
    </div>
  );
}

interface TenantDetail {
  organization: { id: string; name: string; slug: string; createdAt: string };
  members: Array<{ id: string; role: string; user: { name: string; email: string } }>;
  subscription: { planSlug: string | null; status: SubscriptionStatus; currency: string } | null;
  entitlements: Array<{
    serviceKey: string;
    name: string;
    limit: number | null;
    enabled: boolean;
    overridden: boolean;
  }>;
  usage: { entries: Array<{ serviceKey: string; name: string; quantity: number }> };
  invoices: Array<{
    id: string;
    totalMinor: number;
    currency: string;
    status: string;
    periodStart: string;
  }>;
}

function TenantDetail({
  organizationId,
  canWrite,
}: {
  organizationId: string;
  canWrite: boolean;
}): ReactNode {
  const detail = useAsync(
    () => api.get<TenantDetail>(`/api/admin/tenants/${organizationId}`),
    [organizationId],
  );
  const { run, pending, error } = useAction();

  const [serviceKey, setServiceKey] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');

  if (detail.loading) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }
  if (detail.error || !detail.data) return <ErrorBanner error={detail.error} />;

  const tenant = detail.data;
  // A paid invoice cannot be credited — the server refuses it — so it is not
  // offered as a choice.
  const creditable = tenant.invoices.filter((invoice) => invoice.status !== 'paid');

  async function applyOverride(): Promise<void> {
    await run(async () => {
      await api.put(`/api/admin/tenants/${organizationId}/overrides`, {
        serviceKey,
        includedQuantity: quantity === '' ? null : Number(quantity),
        reason,
      });
      setServiceKey('');
      setQuantity('');
      setReason('');
      detail.reload();
    });
  }

  return (
    <>
      <Card title={tenant.organization.name}>
        <div className="grid">
          <div>
            <h3 className="muted">Subscription</h3>
            {tenant.subscription ? (
              <p className="row" style={{ gap: '0.4rem' }}>
                <span style={{ textTransform: 'capitalize' }}>{tenant.subscription.planSlug}</span>
                <StatusBadge status={tenant.subscription.status} />
              </p>
            ) : (
              <p className="muted small">Free plan</p>
            )}
          </div>
          <div>
            <h3 className="muted">Members</h3>
            <ul style={{ margin: 0, paddingLeft: '1rem' }} className="small">
              {tenant.members.map((m) => (
                <li key={m.id}>
                  {m.user.email} <span className="faint">({m.role})</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>

      <Card title="Entitlements">
        <table>
          <tbody>
            {tenant.entitlements.map((entitlement) => (
              <tr key={entitlement.serviceKey}>
                <td>
                  {entitlement.name}
                  {entitlement.overridden && (
                    <span className="badge badge-warning" style={{ marginLeft: '0.5rem' }}>
                      Overridden
                    </span>
                  )}
                </td>
                <td className="num">
                  {!entitlement.enabled
                    ? '—'
                    : entitlement.limit === null
                      ? 'Unlimited'
                      : formatNumber(entitlement.limit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {canWrite && (
        <Card title="Grant an override">
          <ErrorBanner message={error} />
          <p className="small faint" style={{ marginBottom: '0.75rem' }}>
            A reason is required and is recorded in the audit log alongside your name.
          </p>
          <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="ov-service">Service</label>
              <select
                id="ov-service"
                value={serviceKey}
                onChange={(e) => setServiceKey(e.target.value)}
              >
                <option value="">Choose…</option>
                {tenant.entitlements.map((e) => (
                  <option key={e.serviceKey} value={e.serviceKey}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="ov-qty">Included</label>
              <input
                id="ov-qty"
                type="number"
                min={0}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="blank = unlimited"
              />
            </div>
            <div style={{ flex: 2 }}>
              <label htmlFor="ov-reason">Reason</label>
              <input
                id="ov-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Negotiated pilot"
                required
              />
            </div>
            <button
              type="button"
              className="button-primary"
              disabled={pending || !serviceKey || reason.trim().length < 3}
              onClick={() => void applyOverride()}
            >
              {pending && <Spinner />}
              Apply
            </button>
          </div>
        </Card>
      )}

      {tenant.invoices.length > 0 && (
        <Card title="Recent invoices">
          <table>
            <tbody>
              {tenant.invoices.map((invoice) => (
                <tr key={invoice.id}>
                  <td className="muted small">{formatDate(invoice.periodStart)}</td>
                  <td>
                    <span className="badge">{invoice.status}</span>
                  </td>
                  <td className="num">{formatMoney(invoice.totalMinor, invoice.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {canWrite && creditable.length > 0 && (
        <IssueCredit
          organizationId={organizationId}
          invoices={creditable}
          onIssued={detail.reload}
        />
      )}
    </>
  );
}

/**
 * Credit an open invoice.
 *
 * The amount is entered in major units because that is how a support
 * conversation states it ("refund ₹500"), and converted here — the API takes
 * minor units. Only unpaid invoices are offered: crediting a paid one would not
 * move money back, so it would misrepresent what happened.
 */
function IssueCredit({
  organizationId,
  invoices,
  onIssued,
}: {
  organizationId: string;
  invoices: TenantDetail['invoices'];
  onIssued: () => void;
}): ReactNode {
  const { run, pending, error } = useAction();
  const [invoiceId, setInvoiceId] = useState(invoices[0]?.id ?? '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const invoice = invoices.find((row) => row.id === invoiceId) ?? null;
  const exponent = minorUnitExponent(invoice?.currency ?? 'INR');
  const amountMinor = Math.round(Number(amount) * 10 ** exponent);
  const valid =
    invoice !== null &&
    amount !== '' &&
    Number.isSafeInteger(amountMinor) &&
    amountMinor > 0 &&
    amountMinor <= invoice.totalMinor &&
    reason.trim().length >= 3;

  async function issue(): Promise<void> {
    await run(async () => {
      await api.post(`/api/admin/tenants/${organizationId}/credits`, {
        invoiceId,
        amountMinor,
        reason,
      });
      setAmount('');
      setReason('');
      onIssued();
    });
  }

  return (
    <Card title="Credit an invoice">
      <ErrorBanner message={error} />
      <p className="small faint" style={{ marginBottom: '0.75rem' }}>
        Added as a negative line, so the invoice keeps showing what was charged and what was
        credited. A reason is required and is recorded against your name.
      </p>
      <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <label htmlFor="cr-invoice">Invoice</label>
          <select id="cr-invoice" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}>
            {invoices.map((row) => (
              <option key={row.id} value={row.id}>
                {formatDate(row.periodStart)} · {formatMoney(row.totalMinor, row.currency)} ·{' '}
                {row.status}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cr-amount">Amount{invoice ? ` (${invoice.currency})` : ''}</label>
          <input
            id="cr-amount"
            type="number"
            min={0}
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
        </div>
        <div style={{ flex: 2 }}>
          <label htmlFor="cr-reason">Reason</label>
          <input
            id="cr-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Duplicate charge, ticket #412"
            required
          />
        </div>
        <button
          type="button"
          className="button-primary"
          disabled={pending || !valid}
          onClick={() => void issue()}
        >
          {pending && <Spinner />}
          Credit
        </button>
      </div>
      {invoice && amount !== '' && amountMinor > invoice.totalMinor && (
        <p className="field-hint">A credit cannot exceed the invoice total.</p>
      )}
    </Card>
  );
}
